/**
 * LeadsPage.tsx
 * CRM para gestión de leads inmobiliarios
 * Responsive: Cards en móvil, Tabla en desktop
 */

import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Users,
  MessageSquare,
  Search,
  Phone,
  Mail,
  Loader,
  Eye,
  Sparkles,
  Archive,
  ArchiveRestore,
  Trash2,
  UserPlus,
  ArrowUp,
  ArrowDown,
  X,
  Save,
  Calendar,
  Plus,
  CheckCircle,
  Clock,
  AlertCircle,
  Edit3,
  ChevronRight
} from 'lucide-react'
import DashboardLayout from '@/components/DashboardLayout'
import GlassCard from '@/components/ui/GlassCard'
import GradientButton from '@/components/ui/GradientButton'
import Input from '@/components/ui/Input'
import LeadDetailModal from '@/components/LeadDetailModal'
import CreateLeadModal from '@/components/CreateLeadModal'
import LeadsKanban from '@/components/crm/LeadsKanban'
import ActivityFormModal from '@/components/crm/ActivityFormModal'
import { LayoutGrid, Table2 } from 'lucide-react'
import { toast } from 'react-toastify'
import api from '@/services/api'
import { useAuth } from '@/hooks/useAuth'

interface Lead {
  id: string
  name: string
  phone: string
  email: string
  budget_min: number
  budget_max: number
  budget_currency: string
  budget_text: string
  property_type: string
  zones: string[]
  purpose: string
  timeframe: string
  language: string
  country_code: string
  score: number
  temperature: 'hot' | 'warm' | 'cold'
  status: string
  notes: string
  next_contact_date: string
  last_contact_date: string
  interaction_count: number
  tags: string[]
  reclassified: boolean
  conversation_summary: string
  created_at: string
  message_count: number
  archived: boolean
  conversation_id: string
}

interface Conversation {
  id: string
  session_id: string
  messages: any[]
  completed: boolean
  created_at: string
  updated_at: string
  message_count: number
}

interface Activity {
  id: string
  lead_id: string
  agent_id: string
  activity_type: string
  title: string
  description: string
  scheduled_at: string
  completed_at: string | null
  status: 'pending' | 'completed' | 'cancelled' | 'overdue'
  outcome: string | null
  outcome_notes: string | null
  created_at: string
  updated_at: string
  lead_name: string
  lead_phone: string
  lead_email: string
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos', color: 'gray' },
  { value: 'new', label: 'Nuevo', color: 'yellow' },
  { value: 'contacted', label: 'Contactado', color: 'blue' },
  { value: 'qualified', label: 'Calificado', color: 'green' },
  { value: 'negotiating', label: 'Negociando', color: 'orange' },
  { value: 'closed_won', label: 'Ganado', color: 'emerald' },
  { value: 'closed_lost', label: 'Perdido', color: 'red' }
]

const TEMPERATURE_OPTIONS = [
  { value: 'all', label: 'Todas', icon: '🌡️' },
  { value: 'hot', label: 'Caliente', icon: '🔥' },
  { value: 'warm', label: 'Tibio', icon: '☀️' },
  { value: 'cold', label: 'Frío', icon: '❄️' }
]

const ACTIVITY_TYPES = [
  { value: 'call', label: 'Llamada', icon: '📞' },
  { value: 'whatsapp', label: 'WhatsApp', icon: '💬' },
  { value: 'email', label: 'Email', icon: '📧' },
  { value: 'visit', label: 'Visita', icon: '🏠' },
  { value: 'meeting', label: 'Reunión', icon: '🤝' },
  { value: 'property_show', label: 'Mostrar propiedad', icon: '🔑' },
  { value: 'follow_up', label: 'Seguimiento', icon: '📋' },
  { value: 'note', label: 'Nota', icon: '📝' }
]

const ACTIVITY_STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'overdue', label: 'Vencida' },
  { value: 'completed', label: 'Completada' },
  { value: 'cancelled', label: 'Cancelada' }
]

const LeadsPage: React.FC = () => {
  const { t } = useTranslation('crm')
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<'leads' | 'conversations' | 'seguimiento'>(() => {
    const tab = searchParams.get('tab')
    if (tab === 'leads' || tab === 'conversations' || tab === 'seguimiento') return tab
    return 'leads'
  })
  const [leadIdFilter, setLeadIdFilter] = useState<string | null>(() => searchParams.get('lead_id'))
  const [leads, setLeads] = useState<Lead[]>([])
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState('all')
  const [temperatureFilter, setTemperatureFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [sortField, setSortField] = useState<string>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  // Vista del pipeline: Kanban por defecto (más visual); la preferencia se recuerda.
  const [viewMode, setViewMode] = useState<'kanban' | 'table'>(
    () => (localStorage.getItem('crm_view_mode') as 'kanban' | 'table') || 'kanban'
  )
  useEffect(() => { localStorage.setItem('crm_view_mode', viewMode) }, [viewMode])
  // Modal de actividad desde el Kanban: agendar sin salir del tablero (patrón CRM estándar).
  const [activityModalLead, setActivityModalLead] = useState<Lead | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedConversations, setSelectedConversations] = useState<string[]>([])
  const [reanalyzing, setReanalyzing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [viewingConversation, setViewingConversation] = useState<Conversation | null>(null)
  const [creatingLead, setCreatingLead] = useState<Conversation | null>(null)
  const [leadForm, setLeadForm] = useState<any>({})
  const [activities, setActivities] = useState<Activity[]>([])
  const [activityStatusFilter, setActivityStatusFilter] = useState('all')
  const [activityTypeFilter, setActivityTypeFilter] = useState('all')
  const [activitySearchTerm, setActivitySearchTerm] = useState('')
  const [loadingActivities, setLoadingActivities] = useState(false)
  const [showActivityModal, setShowActivityModal] = useState(false)
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null)
  const [activityForm, setActivityForm] = useState<any>({})
  const [savingActivity, setSavingActivity] = useState(false)
  const [leadsForSelect, setLeadsForSelect] = useState<Lead[]>([])

  // Sincronizar URL params con estado
  useEffect(() => {
    const tab = searchParams.get('tab')
    const leadId = searchParams.get('lead_id')

    if (tab === 'leads' || tab === 'conversations' || tab === 'seguimiento') {
      setActiveTab(tab)
    }
    setLeadIdFilter(leadId)
  }, [searchParams])

  useEffect(() => {
    if (activeTab === 'leads') {
      fetchLeads()
    } else if (activeTab === 'conversations') {
      fetchConversations()
    } else if (activeTab === 'seguimiento') {
      fetchActivities()
      fetchLeadsForSelect()
    }
  }, [activeTab, statusFilter, temperatureFilter, searchTerm, showArchived, activityStatusFilter, activityTypeFilter, leadIdFilter])

  const fetchLeads = async () => {
    setLoading(true)
    try {
      const params: any = {}
      if (statusFilter !== 'all') params.status = statusFilter
      if (temperatureFilter !== 'all') params.temperature = temperatureFilter
      if (searchTerm) params.search = searchTerm
      params.show_archived = showArchived
      const response = await api.get('/api/agents/leads', { params })
      setLeads(response.data.leads || [])
    } catch (error) {
      console.error('Error fetching leads:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchConversations = async () => {
    setLoading(true)
    try {
      const response = await api.get('/api/agents/conversations-unqualified')
      setConversations(response.data.conversations || [])
    } catch (error) {
      console.error('Error fetching conversations:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchActivities = async () => {
    setLoadingActivities(true)
    try {
      const params: any = {}
      if (activityStatusFilter !== 'all') params.status = activityStatusFilter
      if (activityTypeFilter !== 'all') params.activity_type = activityTypeFilter
      if (leadIdFilter) params.lead_id = leadIdFilter
      const response = await api.get('/api/agents/activities', { params })
      const fetchedActivities = response.data.activities || []
      setActivities(fetchedActivities)

      if (leadIdFilter && fetchedActivities.length === 0) {
        toast.info('Este lead no tiene actividades programadas. Puedes crear una nueva.')
        setSearchParams({ tab: 'seguimiento' })
        setLeadIdFilter(null)
      }
    } catch (error) {
      console.error('Error fetching activities:', error)
    } finally {
      setLoadingActivities(false)
    }
  }

  const fetchLeadsForSelect = async () => {
    try {
      const response = await api.get('/api/agents/leads', { params: { show_archived: false } })
      setLeadsForSelect(response.data.leads || [])
    } catch (error) {
      console.error('Error fetching leads for select:', error)
    }
  }

  const handleArchiveLead = async (leadId: string, archived: boolean) => {
    try {
      await api.put(`/api/agents/leads/${leadId}/archive`, { archived })
      fetchLeads()
    } catch (error) {
      console.error('Error archiving lead:', error)
      toast.error((error as any).response?.data?.error || 'Error al archivar lead')
    }
  }

  /**
   * Mueve un lead de etapa (Kanban). OPTIMISTA: la tarjeta se mueve al instante
   * y si el API falla se revierte. Un refetch aquí congelaría el drag ~300ms.
   */
  const handleStatusChange = async (leadId: string, newStatus: string) => {
    const previous = leads.find(l => l.id === leadId)?.status
    if (!previous || previous === newStatus) return
    setLeads(prev => prev.map(l => (l.id === leadId ? { ...l, status: newStatus } : l)))
    try {
      await api.put(`/api/agents/leads/${leadId}`, { status: newStatus })
    } catch (error) {
      setLeads(prev => prev.map(l => (l.id === leadId ? { ...l, status: previous } : l)))
      console.error('Error updating lead status:', error)
      toast.error((error as any).response?.data?.error || 'Error al mover el lead')
    }
  }

  const handleReanalyze = async () => {
    if (selectedConversations.length === 0) return
    setReanalyzing(true)
    try {
      const response = await api.post('/api/agents/leads/reanalyze', { conversationIds: selectedConversations })
      toast.success(`Análisis completado: ${response.data.results.newLeadsCreated} nuevos leads creados`)
      setSelectedConversations([])
      fetchConversations()
      fetchLeads()
    } catch (error) {
      console.error('Error reanalyzing:', error)
      toast.error((error as any).response?.data?.error || 'Error al re-analizar conversaciones')
    } finally {
      setReanalyzing(false)
    }
  }

  const handleDeleteConversations = async () => {
    if (selectedConversations.length === 0) return
    if (!confirm(`¿Eliminar ${selectedConversations.length} conversación(es)?`)) return
    setDeleting(true)
    try {
      await Promise.all(selectedConversations.map(id => api.delete(`/api/agents/conversations/${id}`)))
      toast.success(`${selectedConversations.length} conversación(es) eliminada(s)`)
      setSelectedConversations([])
      fetchConversations()
    } catch (error: any) {
      console.error('Error deleting conversations:', error)
      toast.error(error.response?.data?.error || 'Error al eliminar conversaciones')
    } finally {
      setDeleting(false)
    }
  }

  const handleCreateLeadManual = async () => {
    if (!leadForm.name || !leadForm.phone) {
      toast.warning('Nombre y teléfono son requeridos')
      return
    }
    try {
      await api.post('/api/agents/leads/create-manual', { conversationId: creatingLead?.id, ...leadForm })
      toast.success('Lead creado exitosamente')
      setCreatingLead(null)
      setLeadForm({})
      setSelectedConversations([])
      fetchConversations()
      fetchLeads()
    } catch (error) {
      console.error('Error creating lead:', error)
      toast.error((error as any).response?.data?.error || 'Error al crear lead')
    }
  }

  const toggleSelectAll = () => {
    if (selectedConversations.length === conversations.length) {
      setSelectedConversations([])
    } else {
      setSelectedConversations(conversations.map(c => c.id))
    }
  }

  const openNewActivityModal = () => {
    setEditingActivity(null)
    setActivityForm({ lead_id: '', activity_type: 'call', title: '', description: '', scheduled_at: '', scheduled_time: '' })
    setShowActivityModal(true)
  }

  const openEditActivityModal = (activity: Activity) => {
    setEditingActivity(activity)
    const scheduledDate = activity.scheduled_at ? new Date(activity.scheduled_at) : null
    setActivityForm({
      lead_id: activity.lead_id,
      activity_type: activity.activity_type,
      title: activity.title,
      description: activity.description || '',
      scheduled_at: scheduledDate ? scheduledDate.toISOString().split('T')[0] : '',
      scheduled_time: scheduledDate ? scheduledDate.toTimeString().slice(0, 5) : '',
      status: activity.status,
      outcome: activity.outcome || '',
      outcome_notes: activity.outcome_notes || ''
    })
    setShowActivityModal(true)
  }

  const handleSaveActivity = async () => {
    if (!activityForm.lead_id || !activityForm.title || !activityForm.activity_type) {
      toast.warning('Lead, título y tipo son requeridos')
      return
    }
    setSavingActivity(true)
    try {
      let scheduled_at = null
      if (activityForm.scheduled_at) {
        const dateTime = activityForm.scheduled_time
          ? `${activityForm.scheduled_at}T${activityForm.scheduled_time}:00`
          : `${activityForm.scheduled_at}T09:00:00`
        scheduled_at = new Date(dateTime).toISOString()
      }
      if (editingActivity) {
        await api.put(`/api/agents/activities/${editingActivity.id}`, {
          activity_type: activityForm.activity_type,
          title: activityForm.title,
          description: activityForm.description,
          scheduled_at,
          status: activityForm.status,
          outcome: activityForm.outcome,
          outcome_notes: activityForm.outcome_notes
        })
        toast.success('Actividad actualizada')
      } else {
        await api.post(`/api/agents/leads/${activityForm.lead_id}/activities`, {
          activity_type: activityForm.activity_type,
          title: activityForm.title,
          description: activityForm.description,
          scheduled_at
        })
        toast.success('Actividad creada')
      }
      setShowActivityModal(false)
      setActivityForm({})
      setEditingActivity(null)
      fetchActivities()
    } catch (error: any) {
      console.error('Error saving activity:', error)
      toast.error(error.response?.data?.error || 'Error al guardar actividad')
    } finally {
      setSavingActivity(false)
    }
  }

  const handleCompleteActivity = async (activity: Activity) => {
    try {
      await api.put(`/api/agents/activities/${activity.id}`, {
        status: 'completed',
        outcome: 'completed',
        outcome_notes: 'Marcada como completada'
      })
      fetchActivities()
    } catch (error) {
      console.error('Error completing activity:', error)
      toast.error((error as any).response?.data?.error || 'Error al completar actividad')
    }
  }

  const handleDeleteActivity = async (activityId: string) => {
    if (!confirm('¿Eliminar esta actividad?')) return
    try {
      await api.delete(`/api/agents/activities/${activityId}`)
      fetchActivities()
    } catch (error) {
      console.error('Error deleting activity:', error)
      toast.error((error as any).response?.data?.error || 'Error al eliminar actividad')
    }
  }

  const groupedActivities = React.useMemo(() => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)
    const overdue: Activity[] = []
    const todayActs: Activity[] = []
    const upcoming: Activity[] = []
    const completed: Activity[] = []
    const cancelled: Activity[] = []
    let filtered = activities
    if (activitySearchTerm) {
      const search = activitySearchTerm.toLowerCase()
      filtered = activities.filter(a =>
        a.title.toLowerCase().includes(search) ||
        a.lead_name?.toLowerCase().includes(search) ||
        a.description?.toLowerCase().includes(search)
      )
    }
    filtered.forEach(activity => {
      if (activity.status === 'completed') {
        completed.push(activity)
      } else if (activity.status === 'cancelled') {
        cancelled.push(activity)
      } else if (activity.status === 'overdue') {
        overdue.push(activity)
      } else {
        const scheduledDate = activity.scheduled_at ? new Date(activity.scheduled_at) : null
        if (scheduledDate) {
          if (scheduledDate < today) {
            overdue.push(activity)
          } else if (scheduledDate >= today && scheduledDate < tomorrow) {
            todayActs.push(activity)
          } else {
            upcoming.push(activity)
          }
        } else {
          upcoming.push(activity)
        }
      }
    })
    return { overdue, today: todayActs, upcoming, completed, cancelled }
  }, [activities, activitySearchTerm])

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const sortedLeads = React.useMemo(() => {
    return [...leads].sort((a, b) => {
      let aValue: any = a[sortField as keyof Lead]
      let bValue: any = b[sortField as keyof Lead]
      if (aValue === null || aValue === undefined) return sortOrder === 'asc' ? 1 : -1
      if (bValue === null || bValue === undefined) return sortOrder === 'asc' ? -1 : 1
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [leads, sortField, sortOrder])

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null
    return sortOrder === 'asc' ? <ArrowUp className="w-4 h-4 inline ml-1" /> : <ArrowDown className="w-4 h-4 inline ml-1" />
  }

  const getStatusClasses = (status: string): string => {
    const classMap: Record<string, string> = {
      new: 'bg-yellow-500/20 text-yellow-400',
      contacted: 'bg-blue-500/20 text-blue-400',
      qualified: 'bg-green-500/20 text-green-400',
      negotiating: 'bg-orange-500/20 text-orange-400',
      closed_won: 'bg-emerald-500/20 text-emerald-400',
      closed_lost: 'bg-red-500/20 text-red-400'
    }
    return classMap[status] || 'bg-gray-500/20 text-gray-400'
  }

  const getTemperatureIcon = (temp: string) => {
    const option = TEMPERATURE_OPTIONS.find(t => t.value === temp)
    return option?.icon || '🌡️'
  }

  const getActivityIcon = (type: string) => {
    const option = ACTIVITY_TYPES.find(t => t.value === type)
    return option?.icon || '📌'
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Sin fecha'
    return new Date(dateString).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  const formatDateShort = (dateString: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })
  }

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'Sin fecha'
    return new Date(dateString).toLocaleDateString('es-MX', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
  }

  const getConversationName = (conv: Conversation) => {
    const firstUserMsg = conv.messages?.find((m: any) => m.role === 'user')
    if (firstUserMsg?.content) {
      const preview = firstUserMsg.content.slice(0, 30)
      return preview.length < firstUserMsg.content.length ? preview + '...' : preview
    }
    return `Anónimo ${formatTime(conv.created_at)}`
  }

  // Componente LeadCard para vista móvil
  const LeadCard = ({ lead }: { lead: Lead }) => (
    <div className={`p-4 border-b border-white/5 ${lead.archived ? 'opacity-50' : ''}`}>
      {/* Header: Temperatura + Nombre + Score */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="text-xl flex-shrink-0">{getTemperatureIcon(lead.temperature)}</span>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-white truncate">{lead.name || 'Sin nombre'}</p>
            <p className="text-sm text-gray-400 truncate">{lead.phone}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex items-center gap-1">
            <div className="w-10 h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className={`h-full ${lead.score >= 70 ? 'bg-green-500' : lead.score >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`} 
                style={{ width: `${lead.score}%` }} 
              />
            </div>
            <span className="text-xs text-gray-400 w-6">{lead.score}</span>
          </div>
        </div>
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-3">
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusClasses(lead.status)}`}>
          {STATUS_OPTIONS.find(s => s.value === lead.status)?.label || lead.status}
        </span>
        {lead.property_type && (
          <span className="px-2 py-0.5 rounded-full text-xs bg-white/10 text-gray-300">
            {lead.property_type}
          </span>
        )}
        {lead.purpose && (
          <span className="px-2 py-0.5 rounded-full text-xs bg-white/10 text-gray-300">
            {lead.purpose}
          </span>
        )}
        {lead.reclassified && (
          <span className="px-2 py-0.5 rounded-full text-xs bg-purple-500/20 text-purple-400">
            🔄 Re-analizado
          </span>
        )}
        {lead.archived && (
          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-500/20 text-gray-400">
            📦 Archivado
          </span>
        )}
      </div>

      {/* Info row */}
      <div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
        <span>📅 {formatDateShort(lead.created_at)}</span>
        {lead.next_contact_date && (
          <span>⏰ Próx: {formatDateShort(lead.next_contact_date)}</span>
        )}
        <span>{lead.interaction_count} int.</span>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-2 border-t border-white/5">
        <div className="flex items-center gap-1">
          <button 
            onClick={() => { setSelectedLead(lead); setShowDetailModal(true) }} 
            className="p-2.5 hover:bg-blue-500/20 rounded-lg transition-colors" 
            title="Ver detalle"
          >
            <Eye className="w-5 h-5 text-blue-400" />
          </button>
          {lead.phone && (
            <>
              <a 
                href={`https://wa.me/${lead.phone.replace(/\D/g, '')}?text=Hola ${lead.name}, soy tu asesor inmobiliario`} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="p-2.5 hover:bg-green-500/20 rounded-lg transition-colors" 
                title="WhatsApp"
              >
                <MessageSquare className="w-5 h-5 text-green-400" />
              </a>
              <a 
                href={`tel:${lead.phone}`} 
                className="p-2.5 hover:bg-blue-500/20 rounded-lg transition-colors" 
                title="Llamar"
              >
                <Phone className="w-5 h-5 text-blue-400" />
              </a>
            </>
          )}
          {lead.email && (
            <a 
              href={`mailto:${lead.email}`} 
              className="p-2.5 hover:bg-purple-500/20 rounded-lg transition-colors" 
              title="Email"
            >
              <Mail className="w-5 h-5 text-purple-400" />
            </a>
          )}
        </div>
        <button 
          onClick={() => handleArchiveLead(lead.id, !lead.archived)} 
          className="p-2.5 hover:bg-yellow-500/20 rounded-lg transition-colors" 
          title={lead.archived ? 'Desarchivar' : 'Archivar'}
        >
          {lead.archived ? <ArchiveRestore className="w-5 h-5 text-yellow-400" /> : <Archive className="w-5 h-5 text-yellow-400" />}
        </button>
      </div>
    </div>
  )

  const ActivityRow = ({ activity, showStatus = false }: { activity: Activity; showStatus?: boolean }) => (
    <div className="flex items-center justify-between p-3 hover:bg-white/5 rounded-lg transition-colors">
      <div className="flex items-center space-x-3 flex-1 min-w-0">
        <span className="text-xl flex-shrink-0">{getActivityIcon(activity.activity_type)}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-white font-medium truncate">{activity.title}</p>
            {showStatus && activity.status === 'completed' && (
              <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded flex-shrink-0">Completada</span>
            )}
            {showStatus && activity.status === 'cancelled' && (
              <span className="text-xs bg-gray-500/20 text-gray-400 px-2 py-0.5 rounded flex-shrink-0">Cancelada</span>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-400 flex-wrap">
            <span className="truncate">{activity.lead_name || 'Sin lead'}</span>
            <span className="hidden sm:inline">•</span>
            <span className="text-xs sm:text-sm">{formatDateTime(activity.scheduled_at)}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
        {activity.status === 'pending' && (
          <button onClick={() => handleCompleteActivity(activity)} className="p-2 hover:bg-green-500/20 rounded-lg transition-colors" title="Marcar completada">
            <CheckCircle className="w-4 h-4 text-green-400" />
          </button>
        )}
        <button onClick={() => openEditActivityModal(activity)} className="p-2 hover:bg-blue-500/20 rounded-lg transition-colors" title="Editar">
          <Edit3 className="w-4 h-4 text-blue-400" />
        </button>
        <button onClick={() => handleDeleteActivity(activity.id)} className="p-2 hover:bg-red-500/20 rounded-lg transition-colors" title="Eliminar">
          <Trash2 className="w-4 h-4 text-red-400" />
        </button>
      </div>
    </div>
  )

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">{t('title')}</h1>
          <p className="text-gray-400 mt-1 text-sm sm:text-base">{t('subtitle')}</p>
        </div>

        {/* Tabs - Scroll horizontal en móvil */}
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex space-x-1 sm:space-x-2 border-b border-white/10 min-w-max">
            {[
              { id: 'leads', label: t('tabs.leads'), labelShort: t('tabs.leads'), icon: Users },
              { id: 'conversations', label: t('tabs.conversations'), labelShort: t('tabs.conversationsShort'), icon: MessageSquare },
              { id: 'seguimiento', label: t('tabs.seguimiento'), labelShort: t('tabs.seguimiento'), icon: Calendar }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-1.5 sm:space-x-2 px-3 sm:px-6 py-2.5 sm:py-3 font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">
                  <span className="sm:hidden">{tab.labelShort}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab: Leads */}
        {activeTab === 'leads' && (
          <div className="space-y-4">
            {/* Filtros */}
            <GlassCard className="p-3 sm:p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <Input 
                    placeholder={t('filters.search')} 
                    value={searchTerm} 
                    onChange={(e) => setSearchTerm(e.target.value)} 
                    className="pl-9 sm:pl-10 text-sm sm:text-base" 
                  />
                </div>
                {viewMode === 'table' && (
                <select 
                  value={statusFilter} 
                  onChange={(e) => setStatusFilter(e.target.value)} 
                  className="bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm sm:text-base"
                >
                  {STATUS_OPTIONS.map(opt => (<option key={opt.value} value={opt.value}>{t(`status.${opt.value}`)}</option>))}
                </select>
                )}
                <select 
                  value={temperatureFilter} 
                  onChange={(e) => setTemperatureFilter(e.target.value)} 
                  className="bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm sm:text-base"
                >
                  {TEMPERATURE_OPTIONS.map(opt => (<option key={opt.value} value={opt.value}>{opt.icon} {t(`temperature.${opt.value}`)}</option>))}
                </select>
                <label className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg cursor-pointer hover:bg-slate-700">
                  <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="w-4 h-4" />
                  <span className="text-white text-sm">{t('filters.archived')}</span>
                </label>

                <div className="flex bg-slate-800 border border-slate-600 rounded-lg p-1">
                  <button onClick={() => setViewMode('kanban')}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-sm transition-colors ${viewMode === 'kanban' ? 'bg-blue-500/20 text-blue-400' : 'text-gray-400 hover:text-white'}`}>
                    <LayoutGrid className="w-4 h-4" /><span className="hidden sm:inline">{t('view.kanban')}</span>
                  </button>
                  <button onClick={() => setViewMode('table')}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-sm transition-colors ${viewMode === 'table' ? 'bg-blue-500/20 text-blue-400' : 'text-gray-400 hover:text-white'}`}>
                    <Table2 className="w-4 h-4" /><span className="hidden sm:inline">{t('view.table')}</span>
                  </button>
                </div>

                <GradientButton 
                  onClick={() => setShowCreateModal(true)} 
                  className="flex items-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('filters.newLead')}</span>
                  <span className="sm:hidden">{t('filters.newShort')}</span>
                </GradientButton>
              </div>
            </GlassCard>


            {/* Contenido */}
            {viewMode === 'kanban' ? (
              loading ? (
                <div className="flex items-center justify-center p-12"><Loader className="w-8 h-8 animate-spin text-blue-400" /></div>
              ) : (
                <LeadsKanban
                  leads={sortedLeads.filter(l => !l.archived || showArchived)}
                  onStatusChange={handleStatusChange}
                  onOpenDetail={(lead) => { setSelectedLead(lead); setShowDetailModal(true) }}
                  onAddActivity={(lead) => setActivityModalLead(lead)}
                />
              )
            ) : (

            <GlassCard className="overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center p-12">
                  <Loader className="w-8 h-8 animate-spin text-blue-400" />
                </div>
              ) : sortedLeads.length === 0 ? (
                <div className="text-center p-12">
                  <Users className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                  <p className="text-gray-400">{t('empty.leads')}</p>
                </div>
              ) : (
                <>
                  {/* Vista Móvil: Cards */}
                  <div className="lg:hidden divide-y divide-white/5">
                    {sortedLeads.map((lead) => (
                      <LeadCard key={lead.id} lead={lead} />
                    ))}
                  </div>

                  {/* Vista Desktop: Tabla */}
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-white/5 border-b border-white/10">
                        <tr>
                          <th onClick={() => handleSort('name')} className="px-4 py-3 text-left text-sm font-medium text-gray-400 cursor-pointer hover:text-white">
                            Contacto <SortIcon field="name" />
                          </th>
                          <th onClick={() => handleSort('status')} className="px-4 py-3 text-left text-sm font-medium text-gray-400 cursor-pointer hover:text-white">
                            Status <SortIcon field="status" />
                          </th>
                          <th onClick={() => handleSort('temperature')} className="px-4 py-3 text-left text-sm font-medium text-gray-400 cursor-pointer hover:text-white">
                            Temp <SortIcon field="temperature" />
                          </th>
                          <th onClick={() => handleSort('property_type')} className="px-4 py-3 text-left text-sm font-medium text-gray-400 cursor-pointer hover:text-white">
                            Intereses <SortIcon field="property_type" />
                          </th>
                          <th onClick={() => handleSort('created_at')} className="px-4 py-3 text-left text-sm font-medium text-gray-400 cursor-pointer hover:text-white">
                            Fecha Alta <SortIcon field="created_at" />
                          </th>
                          <th onClick={() => handleSort('next_contact_date')} className="px-4 py-3 text-left text-sm font-medium text-gray-400 cursor-pointer hover:text-white">
                            Próx. Contacto <SortIcon field="next_contact_date" />
                          </th>
                          <th onClick={() => handleSort('score')} className="px-4 py-3 text-left text-sm font-medium text-gray-400 cursor-pointer hover:text-white">
                            Score <SortIcon field="score" />
                          </th>
                          <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {sortedLeads.map((lead) => (
                          <tr key={lead.id} className={`hover:bg-white/5 ${lead.archived ? 'opacity-50' : ''}`}>
                            <td className="px-4 py-4">
                              <div>
                                <p className="font-medium text-white">{lead.name || 'Sin nombre'}</p>
                                <p className="text-sm text-gray-400">{lead.phone}</p>
                                {lead.reclassified && <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded mt-1 inline-block">🔄 Re-analizado</span>}
                                {lead.archived && <span className="text-xs bg-gray-500/20 text-gray-400 px-2 py-1 rounded mt-1 inline-block ml-1">📦 Archivado</span>}
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusClasses(lead.status)}`}>
                                {STATUS_OPTIONS.find(s => s.value === lead.status)?.label || lead.status}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-2xl">{getTemperatureIcon(lead.temperature)}</td>
                            <td className="px-4 py-4">
                              <p className="text-sm text-white">{lead.property_type || 'No especificado'}</p>
                              <p className="text-xs text-gray-400">{lead.purpose || 'No especificado'}</p>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-sm text-white">{formatDate(lead.created_at)}</p>
                              <p className="text-xs text-gray-400">{lead.interaction_count} interacciones</p>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-sm text-white">{lead.next_contact_date ? formatDate(lead.next_contact_date) : '-'}</p>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center space-x-1">
                                <div className="w-12 h-2 bg-slate-700 rounded-full overflow-hidden">
                                  <div className={`h-full ${lead.score >= 70 ? 'bg-green-500' : lead.score >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${lead.score}%` }} />
                                </div>
                                <span className="text-xs text-gray-400">{lead.score}</span>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center justify-end space-x-2">
                                <button onClick={() => { setSelectedLead(lead); setShowDetailModal(true) }} className="p-2 hover:bg-blue-500/20 rounded-lg transition-colors" title="Ver detalle">
                                  <Eye className="w-4 h-4 text-blue-400" />
                                </button>
                                {lead.phone && (
                                  <>
                                    <a href={`https://wa.me/${lead.phone.replace(/\D/g, '')}?text=Hola ${lead.name}, soy tu asesor inmobiliario`} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-green-500/20 rounded-lg transition-colors" title="WhatsApp">
                                      <MessageSquare className="w-4 h-4 text-green-400" />
                                    </a>
                                    <a href={`tel:${lead.phone}`} className="p-2 hover:bg-blue-500/20 rounded-lg transition-colors" title="Llamar">
                                      <Phone className="w-4 h-4 text-blue-400" />
                                    </a>
                                  </>
                                )}
                                {lead.email && (
                                  <a href={`mailto:${lead.email}`} className="p-2 hover:bg-purple-500/20 rounded-lg transition-colors" title="Email">
                                    <Mail className="w-4 h-4 text-purple-400" />
                                  </a>
                                )}
                                <button onClick={() => handleArchiveLead(lead.id, !lead.archived)} className="p-2 hover:bg-yellow-500/20 rounded-lg transition-colors" title={lead.archived ? 'Desarchivar' : 'Archivar'}>
                                  {lead.archived ? <ArchiveRestore className="w-4 h-4 text-yellow-400" /> : <Archive className="w-4 h-4 text-yellow-400" />}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </GlassCard>
            )}
          </div>
        )}

        {/* Tab: Conversaciones */}
        {activeTab === 'conversations' && (
          <div className="space-y-4">
            <GlassCard className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3 sm:gap-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={selectedConversations.length === conversations.length && conversations.length > 0} 
                      onChange={toggleSelectAll} 
                      className="w-4 h-4" 
                    />
                    <span className="text-gray-400 text-sm">Todas</span>
                  </label>
                  <p className="text-gray-400 text-sm">{selectedConversations.length}/{conversations.length}</p>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={handleDeleteConversations} 
                    disabled={selectedConversations.length === 0 || deleting}
                    className="flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm transition-colors"
                  >
                    {deleting ? <Loader className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    <span className="hidden sm:inline">Eliminar</span>
                  </button>
                  <GradientButton 
                    onClick={handleReanalyze} 
                    disabled={selectedConversations.length === 0 || reanalyzing}
                    className="text-sm"
                  >
                    {reanalyzing ? <Loader className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                    <span className="hidden sm:inline">Re-analizar</span>
                    <span className="sm:hidden">IA</span>
                  </GradientButton>
                </div>
              </div>
            </GlassCard>

            <GlassCard>
              {loading ? (
                <div className="flex items-center justify-center p-12">
                  <Loader className="w-8 h-8 animate-spin text-blue-400" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center p-12">
                  <MessageSquare className="w-16 h-16 mx-auto text-green-600 mb-4" />
                  <p className="text-gray-400">¡Todas las conversaciones están calificadas!</p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {conversations.map((conv) => (
                    <div key={conv.id} className="p-3 sm:p-4 hover:bg-white/5 flex items-center gap-3 sm:gap-4">
                      <input 
                        type="checkbox" 
                        checked={selectedConversations.includes(conv.id)} 
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedConversations([...selectedConversations, conv.id])
                          } else {
                            setSelectedConversations(selectedConversations.filter(id => id !== conv.id))
                          }
                        }} 
                        className="w-4 h-4 flex-shrink-0" 
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{getConversationName(conv)}</p>
                        <p className="text-sm text-gray-400">{conv.message_count} msgs • {formatDateShort(conv.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button 
                          onClick={() => { setCreatingLead(conv); setLeadForm({}) }} 
                          className="p-2.5 hover:bg-green-500/20 rounded-lg transition-colors" 
                          title="Convertir a lead"
                        >
                          <UserPlus className="w-5 h-5 text-green-400" />
                        </button>
                        <button 
                          onClick={() => setViewingConversation(conv)} 
                          className="p-2.5 hover:bg-blue-500/20 rounded-lg transition-colors" 
                          title="Ver conversación"
                        >
                          <Eye className="w-5 h-5 text-blue-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>
          </div>
        )}

        {/* Tab: Seguimiento */}
        {activeTab === 'seguimiento' && (
          <div className="space-y-4">
            <GlassCard className="p-3 sm:p-4">
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="relative sm:col-span-2 lg:col-span-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input 
                      placeholder={t('filters.search')} 
                      value={activitySearchTerm} 
                      onChange={(e) => setActivitySearchTerm(e.target.value)} 
                      className="pl-9 text-sm" 
                    />
                  </div>
                  <select 
                    value={activityStatusFilter} 
                    onChange={(e) => setActivityStatusFilter(e.target.value)} 
                    className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                  >
                    {ACTIVITY_STATUS_OPTIONS.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                  </select>
                  <select 
                    value={activityTypeFilter} 
                    onChange={(e) => setActivityTypeFilter(e.target.value)} 
                    className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                  >
                    <option value="all">Todos los tipos</option>
                    {ACTIVITY_TYPES.map(opt => (<option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>))}
                  </select>
                  <GradientButton onClick={openNewActivityModal} className="text-sm">
                    <Plus className="w-4 h-4 mr-1.5" />
                    <span className="hidden sm:inline">Nueva Actividad</span>
                    <span className="sm:hidden">Nueva</span>
                  </GradientButton>
                </div>
              </div>
            </GlassCard>

            {leadIdFilter && (
              <div className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-500/20 border border-blue-500/30 rounded-lg">
                <span className="text-blue-400 text-sm flex-1">Filtrando por lead específico</span>
                <button
                  onClick={() => {
                    setLeadIdFilter(null)
                    setSearchParams({ tab: 'seguimiento' })
                  }}
                  className="text-blue-400 hover:text-white p-1 hover:bg-blue-500/30 rounded transition-colors"
                  title="Quitar filtro"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {loadingActivities ? (
              <div className="flex items-center justify-center p-12">
                <Loader className="w-8 h-8 animate-spin text-blue-400" />
              </div>
            ) : activities.length === 0 ? (
              <GlassCard className="text-center p-8 sm:p-12">
                <Calendar className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400 mb-4">No hay actividades programadas</p>
                <GradientButton onClick={openNewActivityModal}>
                  <Plus className="w-4 h-4 mr-2" />Crear actividad
                </GradientButton>
              </GlassCard>
            ) : (
              <div className="space-y-4">
                {groupedActivities.overdue.length > 0 && (
                  <GlassCard className="overflow-hidden">
                    <div className="bg-red-500/10 border-b border-red-500/20 px-3 sm:px-4 py-2.5 sm:py-3">
                      <div className="flex items-center space-x-2">
                        <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
                        <h3 className="font-semibold text-red-400 text-sm sm:text-base">Vencidas ({groupedActivities.overdue.length})</h3>
                      </div>
                    </div>
                    <div className="p-2">{groupedActivities.overdue.map(activity => (<ActivityRow key={activity.id} activity={activity} />))}</div>
                  </GlassCard>
                )}
                {groupedActivities.today.length > 0 && (
                  <GlassCard className="overflow-hidden">
                    <div className="bg-yellow-500/10 border-b border-yellow-500/20 px-3 sm:px-4 py-2.5 sm:py-3">
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
                        <h3 className="font-semibold text-yellow-400 text-sm sm:text-base">Hoy ({groupedActivities.today.length})</h3>
                      </div>
                    </div>
                    <div className="p-2">{groupedActivities.today.map(activity => (<ActivityRow key={activity.id} activity={activity} />))}</div>
                  </GlassCard>
                )}
                {groupedActivities.upcoming.length > 0 && (
                  <GlassCard className="overflow-hidden">
                    <div className="bg-green-500/10 border-b border-green-500/20 px-3 sm:px-4 py-2.5 sm:py-3">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                        <h3 className="font-semibold text-green-400 text-sm sm:text-base">Próximas ({groupedActivities.upcoming.length})</h3>
                      </div>
                    </div>
                    <div className="p-2">{groupedActivities.upcoming.map(activity => (<ActivityRow key={activity.id} activity={activity} />))}</div>
                  </GlassCard>
                )}
                {groupedActivities.completed.length > 0 && (
                  <GlassCard className="overflow-hidden">
                    <div className="bg-emerald-500/10 border-b border-emerald-500/20 px-3 sm:px-4 py-2.5 sm:py-3">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
                        <h3 className="font-semibold text-emerald-400 text-sm sm:text-base">Completadas ({groupedActivities.completed.length})</h3>
                      </div>
                    </div>
                    <div className="p-2">{groupedActivities.completed.map(activity => (<ActivityRow key={activity.id} activity={activity} showStatus />))}</div>
                  </GlassCard>
                )}
                {groupedActivities.cancelled.length > 0 && (
                  <GlassCard className="overflow-hidden">
                    <div className="bg-gray-500/10 border-b border-gray-500/20 px-3 sm:px-4 py-2.5 sm:py-3">
                      <div className="flex items-center space-x-2">
                        <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                        <h3 className="font-semibold text-gray-400 text-sm sm:text-base">Canceladas ({groupedActivities.cancelled.length})</h3>
                      </div>
                    </div>
                    <div className="p-2">{groupedActivities.cancelled.map(activity => (<ActivityRow key={activity.id} activity={activity} showStatus />))}</div>
                  </GlassCard>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal: Lead Detail */}
      {selectedLead && (
        <LeadDetailModal 
          isOpen={showDetailModal} 
          onClose={() => { setShowDetailModal(false); setSelectedLead(null) }} 
          lead={selectedLead} 
          onSave={fetchLeads}
        />
      )}
      {/* Modal Crear Lead */}
      <CreateLeadModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={fetchLeads}
      />

      {/* Modal: Ver Conversación */}
      <AnimatePresence>
        {viewingConversation && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" 
            onClick={() => setViewingConversation(null)}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              exit={{ scale: 0.9, opacity: 0 }} 
              onClick={(e) => e.stopPropagation()} 
              className="bg-slate-900 rounded-xl p-4 sm:p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg sm:text-xl font-bold text-white">Conversación</h3>
                <button onClick={() => setViewingConversation(null)} className="text-gray-400 hover:text-white p-1">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="space-y-3">
                {viewingConversation.messages.map((msg: any, idx: number) => (
                  <div key={idx} className={`p-3 rounded-lg ${msg.role === 'user' ? 'bg-blue-500/20 ml-4 sm:ml-8' : 'bg-white/5 mr-4 sm:mr-8'}`}>
                    <p className="text-xs text-gray-400 mb-1">{msg.role === 'user' ? 'Usuario' : 'Bot'}</p>
                    <p className="text-white text-sm sm:text-base">{msg.content}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal: Crear Lead Manual */}
      <AnimatePresence>
        {creatingLead && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" 
            onClick={() => setCreatingLead(null)}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              exit={{ scale: 0.9, opacity: 0 }} 
              onClick={(e) => e.stopPropagation()} 
              className="bg-slate-900 rounded-xl p-4 sm:p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-lg sm:text-xl font-bold text-white mb-4">Crear Lead Manual</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Nombre *</label>
                  <Input value={leadForm.name || ''} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} placeholder="Nombre del lead" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Teléfono *</label>
                  <Input value={leadForm.phone || ''} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} placeholder="+52 871 123 4567" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Email</label>
                  <Input type="email" value={leadForm.email || ''} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} placeholder="email@ejemplo.com" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Presup. Min</label>
                    <Input type="number" value={leadForm.budget_min || ''} onChange={(e) => setLeadForm({ ...leadForm, budget_min: e.target.value })} placeholder="500000" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Presup. Max</label>
                    <Input type="number" value={leadForm.budget_max || ''} onChange={(e) => setLeadForm({ ...leadForm, budget_max: e.target.value })} placeholder="2000000" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Tipo de Propiedad</label>
                  <select value={leadForm.property_type || ''} onChange={(e) => setLeadForm({ ...leadForm, property_type: e.target.value })} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white">
                    <option value="">Seleccionar...</option>
                    <option value="casa">Casa</option>
                    <option value="departamento">Departamento</option>
                    <option value="terreno">Terreno</option>
                    <option value="comercial">Comercial</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Propósito</label>
                  <select value={leadForm.purpose || ''} onChange={(e) => setLeadForm({ ...leadForm, purpose: e.target.value })} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white">
                    <option value="">Seleccionar...</option>
                    <option value="compra">Compra</option>
                    <option value="renta">Renta</option>
                    <option value="inversion">Inversión</option>
                    <option value="leasing">Leasing</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Notas</label>
                  <textarea value={leadForm.notes || ''} onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })} rows={3} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white" placeholder="Notas adicionales..." />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <GradientButton onClick={handleCreateLeadManual} className="flex-1">
                  <Save className="w-4 h-4 mr-2" />Crear Lead
                </GradientButton>
                <button onClick={() => setCreatingLead(null)} className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors">
                  Cancelar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal: Nueva/Editar Actividad */}
      <AnimatePresence>
        {showActivityModal && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" 
            onClick={() => setShowActivityModal(false)}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              exit={{ scale: 0.9, opacity: 0 }} 
              onClick={(e) => e.stopPropagation()} 
              className="bg-slate-900 rounded-xl p-4 sm:p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-lg sm:text-xl font-bold text-white mb-4">{editingActivity ? 'Editar Actividad' : 'Nueva Actividad'}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Lead *</label>
                  <select 
                    value={activityForm.lead_id || ''} 
                    onChange={(e) => setActivityForm({ ...activityForm, lead_id: e.target.value })} 
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white" 
                    disabled={!!editingActivity}
                  >
                    <option value="">Seleccionar lead...</option>
                    {leadsForSelect.map(lead => (
                      <option key={lead.id} value={lead.id}>{lead.name || 'Sin nombre'} - {lead.phone || 'Sin teléfono'}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Tipo *</label>
                  <select value={activityForm.activity_type || 'call'} onChange={(e) => setActivityForm({ ...activityForm, activity_type: e.target.value })} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white">
                    {ACTIVITY_TYPES.map(opt => (<option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Título *</label>
                  <Input value={activityForm.title || ''} onChange={(e) => setActivityForm({ ...activityForm, title: e.target.value })} placeholder="Ej: Llamar para confirmar interés" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Descripción</label>
                  <textarea value={activityForm.description || ''} onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })} rows={2} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white" placeholder="Detalles adicionales..." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Fecha</label>
                    <Input type="date" value={activityForm.scheduled_at || ''} onChange={(e) => setActivityForm({ ...activityForm, scheduled_at: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Hora</label>
                    <Input type="time" value={activityForm.scheduled_time || ''} onChange={(e) => setActivityForm({ ...activityForm, scheduled_time: e.target.value })} />
                  </div>
                </div>
                {editingActivity && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Estado</label>
                      <select value={activityForm.status || 'pending'} onChange={(e) => setActivityForm({ ...activityForm, status: e.target.value })} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white">
                        <option value="pending">Pendiente</option>
                        <option value="completed">Completada</option>
                        <option value="cancelled">Cancelada</option>
                      </select>
                    </div>
                    {activityForm.status === 'completed' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">Resultado</label>
                          <select value={activityForm.outcome || ''} onChange={(e) => setActivityForm({ ...activityForm, outcome: e.target.value })} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white">
                            <option value="">Seleccionar...</option>
                            <option value="successful">Exitoso</option>
                            <option value="no_answer">No contestó</option>
                            <option value="rescheduled">Reprogramado</option>
                            <option value="not_interested">No interesado</option>
                            <option value="other">Otro</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">Notas del resultado</label>
                          <textarea value={activityForm.outcome_notes || ''} onChange={(e) => setActivityForm({ ...activityForm, outcome_notes: e.target.value })} rows={2} className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white" placeholder="¿Qué pasó en esta actividad?" />
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
              <div className="flex gap-3 mt-6">
                <GradientButton onClick={handleSaveActivity} className="flex-1" disabled={savingActivity}>
                  {savingActivity ? (
                    <><Loader className="w-4 h-4 animate-spin mr-2" /> Guardando...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> {editingActivity ? 'Guardar' : 'Crear'}</>
                  )}
                </GradientButton>
                <button onClick={() => setShowActivityModal(false)} className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors">
                  Cancelar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <ActivityFormModal
        isOpen={!!activityModalLead}
        onClose={() => setActivityModalLead(null)}
        onSaved={() => { fetchLeads(); fetchActivities() }}
        leadId={activityModalLead?.id || ''}
        leadName={activityModalLead?.name || ''}
      />
    </DashboardLayout>
  )
}

// ─────────────────────────────────────────────────────────────
// Wrapper de plan — guard para Starter
// LeadsPage (CRM) requiere plan Smart o superior.
// Si el agente es Starter → pantalla de upgrade sin romper hooks.
// Para planes futuros sin CRM, agregar aquí.
// ─────────────────────────────────────────────────────────────
const LeadsPageGuard: React.FC = () => {
  const { agent } = useAuth()
  const plan = agent?.plan?.toLowerCase() || 'free'

  if (plan === 'starter') {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <div className="w-20 h-20 rounded-full bg-purple-500/20 flex items-center justify-center mb-6">
            <Users className="w-10 h-10 text-purple-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">CRM de Leads</h2>
          <p className="text-white/60 mb-2 max-w-md">
            El CRM para gestionar y dar seguimiento a tus leads está disponible desde el plan Smart.
          </p>
          <p className="text-white/40 text-sm mb-8 max-w-md">
            Con Smart obtienes Agente IA 24/7, CRM completo, Analytics y 25 propiedades.
          </p>
          <a
            href="/dashboard/billing"
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-xl text-white font-semibold transition-all shadow-lg"
          >
            Ver planes — desde $789 MXN/mes
          </a>
        </div>
      </DashboardLayout>
    )
  }

  return <LeadsPage />
}

export default LeadsPageGuard
